import { GridReadyEvent } from 'ag-grid-community';
import { ColDef } from 'ag-grid-community/dist/types/src/entities/colDef';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useMemo, useRef } from 'react';
import Text, { TextVariant } from 'src/components/atoms/Text';
import useAvailableProperties from 'src/features/owner/hooks/useAvailableProperties';
import useOwnerProperties from 'src/features/owner/hooks/useOwnerProperties';
import useSyncOwnerProperties from 'src/features/owner/hooks/useSyncOwnerProperties';
import { IAvailableProperty } from 'src/features/owner/ts/interfaces/Owner';
import InputSearch from '../InputSearch';
import Modal, { ModalVariant, ModalWidth } from '../Modal';
import { PaginationControls } from '../PaginationControls';
import PropertySelectionProvider, { usePropertySelection } from './PropertySelectionProvider';
import { gridStyle, ModalGridContainer, ModalSearch, ModalText } from './styles';

interface ILinkPropertyOwnerModalProps {
  ownerId: string;
  isOpen: boolean;
  onConfirm?: () => void;
  closeModal: () => void;
}

// Ensures that whatever type is passed in includes minimum needded fields for this component
interface IProperty {
  id?: string;
  name: string;
  company_name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}
interface IPropertyRowData {
  id?: string;
  propertyName: string;
  companyName: string;
  address: string;
  isPreSelected?: boolean;
}

const transformPropertiesToRowData = (
  properties: IProperty[],
  selectedIds: Set<string>
): IPropertyRowData[] => {
  return properties.map((property) => ({
    id: property.id,
    propertyName: property.name || 'N/A',
    companyName: property.company_name || 'N/A',
    address: `${property.address}, ${property.city}, ${property.state} ${property.zip}`,
    isPreSelected: property.id ? selectedIds.has(property.id) : false
  }));
};

const LinkPropertyOwnerModalContent = ({
  ownerId,
  isOpen,
  onConfirm,
  closeModal
}: ILinkPropertyOwnerModalProps) => {
  const { isSyncing, error, syncProperties } = useSyncOwnerProperties(ownerId);
  const {
    properties: unownedProperties,
    isLoading: isUnownedPropertiesLoading,
    rowsPerPage,
    hasNextPage,
    hasPreviousPage,
    searchQuery,
    totalPages,
    page,
    count,
    onGetAll,
    onSearch
  } = useAvailableProperties({ ownerId });
  const {
    properties: ownerProperties,
    fetchAllProperties,
    fetchAllPropertiesHasLoaded
  } = useOwnerProperties(ownerId);
  const {
    selectedPropertyIds,
    setInitialSelections,
    initialPropertyIds,
    setSelections,
    clearSelections,
    resetToInitial,
    hasChanges,
    getSelectedIds
  } = usePropertySelection();

  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => {
    if (isOpen && !fetchAllPropertiesHasLoaded) {
      fetchAllProperties();
    }
  }, [isOpen, fetchAllProperties, fetchAllPropertiesHasLoaded]);

  useEffect(() => {
    // Only proceed if modal is open and we have fetched owner properties
    if (isOpen && fetchAllPropertiesHasLoaded && ownerProperties.length > 0) {
      const ownerPropertyIds = ownerProperties.map((prop) => prop.property_id).filter(Boolean);
      const isDifferent = (() => {
        if (ownerPropertyIds.length !== initialPropertyIds.size) return true;
        for (const id of ownerPropertyIds) {
          if (!initialPropertyIds.has(id)) return true;
        }
        return false;
      })();

      if (!isDifferent) return;
      setInitialSelections(ownerPropertyIds);
    }
  }, [
    isOpen,
    ownerProperties,
    initialPropertyIds,
    setInitialSelections,
    fetchAllPropertiesHasLoaded
  ]);

  const isUpdatingSelection = useRef(false);

  const rowData = useMemo(() => {
    if (unownedProperties && Array.isArray(unownedProperties) && unownedProperties.length > 0) {
      return transformPropertiesToRowData(unownedProperties, selectedPropertyIds);
    }
    return [];
  }, [unownedProperties, selectedPropertyIds]);

  const handleGridReady = (params: GridReadyEvent) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
    }
  };

  useEffect(() => {
    if (gridRef.current?.api && rowData.length > 0) {
      isUpdatingSelection.current = true;

      setTimeout(() => {
        gridRef.current?.api?.forEachNode((node) => {
          if (node.data?.isPreSelected) {
            node.setSelected(true);
          } else {
            node.setSelected(false);
          }
        });

        setTimeout(() => {
          isUpdatingSelection.current = false;
          // this timeout should be longer than the timeout that wraps this one.
          // Note that these timeout have been chosen to ensure proper sequencing of selection updates.
          // The lenght of time has been chosen to account for rendering delays and state updates, especially during tests.
        }, 35);
      }, 15);
    }
  }, [rowData]);

  const colDefs: ColDef[] = [
    {
      headerName: '',
      field: 'selected',
      checkboxSelection: true,
      headerCheckboxSelection: false,
      width: 50,
      pinned: 'left',
      suppressHeaderMenuButton: true,
      sortable: false
    },
    {
      field: 'propertyName',
      headerName: 'Property name',
      flex: 1,
      suppressHeaderMenuButton: true
    },
    {
      field: 'companyName',
      headerName: 'Company name',
      flex: 1,
      suppressHeaderMenuButton: true
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 2,
      suppressHeaderMenuButton: true
    }
  ];

  return (
    <Modal
      title="Link Properties to Owner"
      labelButtonCancel="Cancel"
      labelButtonConfirm={'Save and Link Properties'}
      showModal={isOpen}
      isBackClosable={false}
      isEscapeClosable={false}
      isDisabledButtonCancel={isSyncing}
      isDisabledButtonConfirm={isSyncing || !hasChanges}
      isLoadingButtonConfirm={isSyncing}
      variant={ModalVariant.primary}
      onConfirm={async () => {
        const selectedIds = getSelectedIds();

        if (syncProperties) {
          await syncProperties(selectedIds);
          onConfirm?.();
          closeModal();
        }
      }}
      onCancel={() => {
        clearSelections();
        resetToInitial();
        closeModal();
      }}
      width={ModalWidth.extraLarge}
      data-testid="link-properties-modal"
      scrollableContent={true}
    >
      <div>
        <ModalText>
          <Text variant={TextVariant.normal}>
            The table lists only the properties that are not currently linked to an Owner.
          </Text>
          <Text variant={TextVariant.normal}>1. Select the properties you want to link.</Text>
          <Text variant={TextVariant.normal}>
            2. Click &quot;Save and Link Properties&quot; to complete the linking process.
          </Text>
          <Text variant={TextVariant.normal}>
            If a property you&apos;re looking for does not appear in this list, navigate to that
            property&apos;s individual page and select the appropriate Owner from the dropdown menu
            there.
          </Text>
        </ModalText>
        <ModalSearch>
          <InputSearch
            onSearch={({ value }) => onSearch(value)}
            onClear={() => onSearch('')}
            name="PropertiesSearch"
            value={searchQuery || ''}
            placeholder="Search"
          />{' '}
        </ModalSearch>
        <ModalGridContainer>
          <div className="ag-theme-alpine" style={gridStyle}>
            <AgGridReact
              ref={gridRef}
              suppressRowClickSelection
              rowData={rowData}
              columnDefs={colDefs}
              loading={isUnownedPropertiesLoading}
              pagination={true}
              paginationPageSize={rowsPerPage}
              paginationPageSizeSelector={[10, 25, 50]}
              suppressPaginationPanel={true}
              rowSelection={'multiple'}
              getRowId={(params) => params.data.id}
              onGridReady={handleGridReady}
              onSelectionChanged={(result) => {
                if (isUpdatingSelection.current) {
                  return;
                }

                const selectedRows = result.api.getSelectedRows();
                const currentSelectedIds: string[] = selectedRows
                  .map((row: IPropertyRowData) => row.id)
                  .filter(Boolean) as string[];

                const allPropertyIdsOnPage: string[] = unownedProperties
                  .map((prop: IAvailableProperty) => prop.id || '')
                  .filter(Boolean);

                const removedIds = allPropertyIdsOnPage.filter(
                  (id: string) => !currentSelectedIds.includes(id)
                );

                const newlyAddedIds = currentSelectedIds;

                setSelections({ added: newlyAddedIds, removed: removedIds });
              }}
              suppressHorizontalScroll={true}
              suppressRowHoverHighlight={true}
              suppressCellFocus={true}
              domLayout="normal"
              suppressScrollOnNewData={true}
              animateRows={false}
            />
          </div>
        </ModalGridContainer>

        <PaginationControls
          currentPage={page}
          rowsPerPage={rowsPerPage}
          totalCount={count}
          totalPages={totalPages}
          setPageSize={(pageSize) => onGetAll(page, pageSize)}
          fetchNextPage={() => onGetAll(page + 1, rowsPerPage)}
          fetchPreviousPage={() => onGetAll(page - 1, rowsPerPage)}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          isLoading={isUnownedPropertiesLoading}
          itemDescriptor="properties"
        />
      </div>
      {error ? `Error: ${error.toString()}` : null}
    </Modal>
  );
};

// Wrapper component that provides the context
const LinkPropertyOwnerModal = (props: ILinkPropertyOwnerModalProps) => {
  return (
    <PropertySelectionProvider>
      <LinkPropertyOwnerModalContent {...props} />
    </PropertySelectionProvider>
  );
};

export default LinkPropertyOwnerModal;
